import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getCustomers, 
  getProducts, 
  getCustomerProductMappings,
  createCustomerProductMapping,
  updateCustomerProductMapping,
  deactivateCustomerProductMapping,
  getCoBrandings
} from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { SearchableSelect } from '../components/ui/searchable-select';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Edit2, 
  Trash2, 
  Loader2, 
  Package,
  Users,
  Tags,
  RefreshCw,
  AlertTriangle,
  Settings2
} from 'lucide-react';

export default function CustomerProductCodes() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [mappings, setMappings] = useState([]);
  const [total, setTotal] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [cobrandings, setCobrandings] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [customerFilter, setCustomerFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMapping, setEditMapping] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formCustomerId, setFormCustomerId] = useState('');
  const [formProductId, setFormProductId] = useState('');
  const [formSupplierCode, setFormSupplierCode] = useState('');
  const [formCobrandingId, setFormCobrandingId] = useState('');
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [customersRes, productsRes, mappingsRes, cobrandingsRes] = await Promise.allSettled([
        getCustomers(),
        getProducts(),
        getCustomerProductMappings({ limit: 500 }),
        getCoBrandings()
      ]);
      
      setCustomers(customersRes.status === 'fulfilled' ? (customersRes.value.data || []) : []);
      setProducts(productsRes.status === 'fulfilled' ? (productsRes.value.data || []) : []);
      setCobrandings(cobrandingsRes.status === 'fulfilled' ? (cobrandingsRes.value.data || []) : []);
      
      if (mappingsRes.status === 'fulfilled') {
        setMappings(mappingsRes.value.data.mappings || []);
        setTotal(mappingsRes.value.data.total || 0);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Filter mappings
  const filteredMappings = useMemo(() => {
    return mappings.filter(m => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          m.supplier_product_code?.toLowerCase().includes(query) ||
          m.customer_name?.toLowerCase().includes(query) ||
          m.product_name?.toLowerCase().includes(query) ||
          m.product_sku?.toLowerCase().includes(query) ||
          m.cobranding_name?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      // Customer filter
      if (customerFilter !== 'all' && m.customer_id !== customerFilter) return false;
      
      // Product filter
      if (productFilter !== 'all' && m.internal_product_id !== productFilter) return false;
      
      return true;
    });
  }, [mappings, searchQuery, customerFilter, productFilter]);

  const openCreateDialog = () => {
    setEditMapping(null);
    setFormCustomerId('');
    setFormProductId('');
    setFormSupplierCode('');
    setFormCobrandingId('');
    setDialogOpen(true);
  };

  const openEditDialog = (mapping) => {
    setEditMapping(mapping);
    setFormCustomerId(mapping.customer_id);
    setFormProductId(mapping.internal_product_id);
    setFormSupplierCode(mapping.supplier_product_code);
    setFormCobrandingId(mapping.cobranding_id || '');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formCustomerId || !formProductId || !formSupplierCode.trim()) {
      toast.error('Customer, Product, and Customer Code are required');
      return;
    }
    
    setSaving(true);
    try {
      if (editMapping) {
        // Update existing
        await updateCustomerProductMapping(editMapping.id, {
          supplier_product_code: formSupplierCode.trim(),
          cobranding_id: formCobrandingId || null
        });
        toast.success('Mapping updated successfully');
      } else {
        // Create new
        await createCustomerProductMapping({
          customer_id: formCustomerId,
          internal_product_id: formProductId,
          supplier_product_code: formSupplierCode.trim(),
          cobranding_id: formCobrandingId || null
        });
        toast.success('Mapping created successfully');
      }
      
      setDialogOpen(false);
      loadData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to save mapping';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (mapping) => {
    setMappingToDelete(mapping);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!mappingToDelete) return;
    
    setDeleting(true);
    try {
      await deactivateCustomerProductMapping(mappingToDelete.id);
      toast.success('Mapping deactivated successfully');
      setDeleteDialogOpen(false);
      setMappingToDelete(null);
      loadData();
    } catch (error) {
      toast.error('Failed to deactivate mapping');
    } finally {
      setDeleting(false);
    }
  };

  const getSelectedCustomerName = () => {
    const customer = customers.find(c => c.id === formCustomerId);
    return customer?.name || '';
  };

  const getSelectedProductName = () => {
    const product = products.find(p => p.id === formProductId);
    return product?.name || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="supplier-product-codes-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900">Customer Product Codes</h1>
          <p className="text-sm text-zinc-500 mt-1">
            Manage customer-specific product codes for dispatch notes and traceability
          </p>
        </div>
        <Button onClick={openCreateDialog} data-testid="add-mapping-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Mapping
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Tags className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{total}</p>
                <p className="text-sm text-zinc-500">Total Mappings</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{new Set(mappings.map(m => m.customer_id)).size}</p>
                <p className="text-sm text-zinc-500">Customers with Codes</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Package className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{new Set(mappings.map(m => m.internal_product_id)).size}</p>
                <p className="text-sm text-zinc-500">Products with Codes</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs text-zinc-500 mb-1 block">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                <Input
                  placeholder="Search customer code, customer, product..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-500 mb-1 block">Customer</Label>
              <Select value={customerFilter} onValueChange={setCustomerFilter}>
                <SelectTrigger data-testid="customer-filter">
                  <SelectValue placeholder="All Customers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Customers</SelectItem>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-500 mb-1 block">Product</Label>
              <Select value={productFilter} onValueChange={setProductFilter}>
                <SelectTrigger data-testid="product-filter">
                  <SelectValue placeholder="All Products" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Products</SelectItem>
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={loadData} className="w-full">
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mappings Table */}
      <Card>
        <CardHeader>
          <CardTitle>Product Code Mappings</CardTitle>
          <CardDescription>
            {filteredMappings.length} mapping{filteredMappings.length !== 1 ? 's' : ''} found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {filteredMappings.length === 0 ? (
            <div className="text-center py-12">
              <Tags className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
              <p className="text-zinc-500">No mappings found</p>
              <p className="text-sm text-zinc-400 mt-1">
                {mappings.length === 0 
                  ? 'Click "Add Mapping" to create your first customer product code'
                  : 'Try adjusting your filters'}
              </p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-zinc-50">
                    <TableHead className="font-semibold">Customer</TableHead>
                    <TableHead className="font-semibold">Internal Product</TableHead>
                    <TableHead className="font-semibold">Packaging</TableHead>
                    <TableHead className="font-semibold">Customer Product Code</TableHead>
                    <TableHead className="font-semibold text-center">Formula</TableHead>
                    <TableHead className="font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMappings.map((mapping) => (
                    <TableRow key={mapping.id} data-testid={`mapping-row-${mapping.id}`}>
                      <TableCell className="font-medium">{mapping.customer_name}</TableCell>
                      <TableCell>
                        <div>{mapping.product_name}</div>
                        {mapping.product_sku && (
                          <Badge variant="outline" className="font-mono text-xs mt-1">
                            {mapping.product_sku}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {mapping.cobranding_name ? (
                          <Badge variant="secondary" className="text-xs">
                            {mapping.cobranding_name}
                          </Badge>
                        ) : (
                          <span className="text-zinc-400 text-sm">Standard</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-blue-100 text-blue-800 font-mono">
                          {mapping.supplier_product_code}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {mapping.unit_weight_kg && mapping.units_per_case ? (
                          <Badge variant="outline" className="text-emerald-700 border-emerald-300 bg-emerald-50 text-xs">
                            {mapping.unit_weight_kg}kg / {mapping.units_per_case} per case
                          </Badge>
                        ) : (
                          <span className="text-xs text-zinc-400">Not set</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/supplier-codes/${mapping.id}/formula`)}
                            title="Configure formula"
                            data-testid={`configure-mapping-${mapping.id}`}
                          >
                            <Settings2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openEditDialog(mapping)}
                            data-testid={`edit-mapping-${mapping.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => confirmDelete(mapping)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-mapping-${mapping.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editMapping ? 'Edit Customer Product Code' : 'Add Customer Product Code'}
            </DialogTitle>
            <DialogDescription>
              {editMapping 
                ? 'Update the customer product code for this mapping'
                : 'Create a new customer-specific product code mapping. Same product can have different codes for different packaging.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="customer">Customer *</Label>
              {editMapping ? (
                <Input 
                  value={getSelectedCustomerName()} 
                  disabled 
                  className="bg-zinc-50 mt-1"
                />
              ) : (
                <SearchableSelect
                  options={customers.map(c => ({ value: c.id, label: c.name }))}
                  value={formCustomerId}
                  onValueChange={setFormCustomerId}
                  placeholder="Select customer..."
                  className="mt-1"
                  data-testid="form-customer-select"
                />
              )}
            </div>
            
            <div>
              <Label htmlFor="product">Internal Product *</Label>
              {editMapping ? (
                <Input 
                  value={getSelectedProductName()} 
                  disabled 
                  className="bg-zinc-50 mt-1"
                />
              ) : (
                <SearchableSelect
                  options={products.map(p => ({ value: p.id, label: `${p.name} (${p.sku})` }))}
                  value={formProductId}
                  onValueChange={setFormProductId}
                  placeholder="Select product..."
                  className="mt-1"
                  data-testid="form-product-select"
                />
              )}
            </div>
            
            <div>
              <Label htmlFor="packaging">Packaging/Brand (Optional)</Label>
              <Select value={formCobrandingId || 'none'} onValueChange={(v) => setFormCobrandingId(v === 'none' ? '' : v)}>
                <SelectTrigger className="mt-1" data-testid="form-packaging-select">
                  <SelectValue placeholder="Standard (no packaging)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Standard (no specific packaging)</SelectItem>
                  {cobrandings.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500 mt-1">
                Same product can have different customer codes for different packaging
              </p>
            </div>
            
            <div>
              <Label htmlFor="supplierCode">Customer Product Code *</Label>
              <Input
                id="supplierCode"
                value={formSupplierCode}
                onChange={(e) => setFormSupplierCode(e.target.value)}
                placeholder="e.g., CB-BLU-250G"
                className="mt-1 font-mono"
                data-testid="form-supplier-code"
              />
              <p className="text-xs text-zinc-500 mt-1">
                This code will appear on dispatch notes for this customer
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-mapping-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editMapping ? 'Update' : 'Create'} Mapping
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Deactivate Mapping
            </DialogTitle>
            <DialogDescription>
              This will deactivate the customer product code mapping. Historical dispatches will retain their original codes.
            </DialogDescription>
          </DialogHeader>
          
          {mappingToDelete && (
            <div className="py-4 space-y-2">
              <div className="bg-zinc-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-zinc-500">Customer:</span>
                  <span className="text-sm font-medium">{mappingToDelete.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-zinc-500">Product:</span>
                  <span className="text-sm font-medium">{mappingToDelete.product_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-zinc-500">Customer Code:</span>
                  <Badge className="bg-blue-100 text-blue-800 font-mono">
                    {mappingToDelete.supplier_product_code}
                  </Badge>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete} 
              disabled={deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Deactivate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
